import { useEffect, useState } from "react";
import {
  MotifButton,
  MotifFormField,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { resetSearchUsers } from "../../store/slices/CTDBUserListSlice";
import "./AddEngagementProxyModal.scss";
import SingleSelectUserSearch from "../Form/SingleSelectUserSearch";

export interface AddEngagementProxyUser {
  Name: string;
  Email: string;
}

interface AddEngagementProxyModalProps {
  show: boolean;
  onClose: () => void;
  onAdd: (user: AddEngagementProxyUser) => void;
  existingEmails: string[];
  focusTrapPaused?: boolean;
}

const AddEngagementProxyModal = ({
  show,
  onClose,
  onAdd,
  existingEmails,
  focusTrapPaused = false,
}: AddEngagementProxyModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedUsers, setSelectedUsers] = useState<
    AddEngagementProxyUser[]
  >([]);
  const [validationError, setValidationError] = useState("");
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (show) {
      dispatch(resetSearchUsers());
      return;
    }

    setSelectedUsers([]);
    setValidationError("");
    setSearchValue("");
    dispatch(resetSearchUsers());
  }, [dispatch, show]);

  const isDuplicateEmail = (email: string) =>
    existingEmails.some(
      (existingEmail) =>
        existingEmail.trim().toLowerCase() === email.trim().toLowerCase(),
    );

  const handleSelectedUsersChange = (users: AddEngagementProxyUser[]) => {
    setSelectedUsers(users);
    setValidationError("");
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setValidationError("");
  };

  const handleCancel = () => {
    onClose();
  };

  const handleAdd = () => {
    const selectedUser = selectedUsers[0];
    if (!selectedUser) {
      setValidationError(
        searchValue.trim()
          ? "Please enter valid user name or email."
          : "Please enter user name or email.",
      );
      return;
    }

    if (isDuplicateEmail(selectedUser.Email)) {
      setValidationError("This username already exists.");
      return;
    }

    onAdd(selectedUser);
    onClose();
  };

  return (
    <MotifModal
      show={show}
      onClose={handleCancel}
      focusTrapPaused={focusTrapPaused}
      size="md"
      className="add-engagement-proxy-modal"
    >
      <MotifModalHeader>Add engagement proxy</MotifModalHeader>
      <MotifModalBody>
        <div className="add-engagement-proxy-content">
          <MotifFormField className="add-engagement-proxy-user-field">
            <label
              id="add-engagement-proxy-user-label"
              htmlFor="add-engagement-proxy-user"
            >
              User
            </label>
            <SingleSelectUserSearch
              proxyOwners={selectedUsers}
              setProductContacts={handleSelectedUsersChange}
              name="add-engagement-proxy-user"
              onSearchChange={handleSearchChange}
              validationMessage={validationError}
            />
          </MotifFormField>
        </div>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          type="button"
          size="medium"
          variant="primary-alt"
          onClick={handleCancel}
        >
          Cancel
        </MotifButton>
        <MotifButton
          type="button"
          size="medium"
          variant="primary"
          onClick={handleAdd}
        >
          Add engagement proxy
        </MotifButton>
      </MotifModalFooter>
    </MotifModal>
  );
};

export default AddEngagementProxyModal;
